import React, { useEffect, useMemo, useRef, useState } from 'react';
import './UpgradeModal.css';
import { IoMdClose } from 'react-icons/io';
import { IoSearch } from 'react-icons/io5';
import axios from 'axios';
import { toast } from 'react-toastify';

// UPDATED: Added fallback URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const readToken = () => {
  try {
    const raw = sessionStorage.getItem('tokens') || sessionStorage.getItem('token');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object')
        return parsed.token || parsed.accessToken || null;
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
  return null;
};

const headersWithAuth = (token) => {
  const h = { accept: '*/*', 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const UpgradeModal = ({ closeModal, branchList = [], onSwapSuccess, allBranches }) => {
  const [currentRole, setCurrentRole] = useState('');
  const [role, setRole] = useState('');
  const [fromBranch, setFromBranch] = useState('');
  const [toBranch, setToBranch] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [currentDesignation, setCurrentDesignation] = useState('');
  const [assignCasesTo, setAssignCasesTo] = useState(''); // NEW FIELD
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [swapResult, setSwapResult] = useState(null);
  const [canForce, setCanForce] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const lastPayloadRef = useRef(null);
  const [isForce, setIsForce] = useState(false);

  // Branch search states
  const [fromBranchSearch, setFromBranchSearch] = useState('');
  const [toBranchSearch, setToBranchSearch] = useState('');
  const [filteredFromBranches, setFilteredFromBranches] = useState([]);
  const [filteredToBranches, setFilteredToBranches] = useState([]);
  const [showFromBranchDropdown, setShowFromBranchDropdown] = useState(false);
  const [showToBranchDropdown, setShowToBranchDropdown] = useState(false);
  const [selectedFromBranchDisplay, setSelectedFromBranchDisplay] =
    useState('');
  const [selectedToBranchDisplay, setSelectedToBranchDisplay] = useState('');

  // Validation
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [officerReport, setOfficerReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fromBranchInputRef = useRef(null);
  const toBranchInputRef = useRef(null);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  const dropdownOptions = useMemo(
    () =>
      // (branchList || []).map((b) => ({
      (allBranches || []).map((b) => ({
        value: b.branch_code,
        label: `${b.branch_name || 'N/A'}: ${b.branch_code}`,
        raw: b,
      })),
    // [branchList]
    [allBranches]
  );

  const [employeeList, setEmployeeList] = useState([]);
  const [assignCasesOptions, setAssignCasesOptions] = useState([]);
  const [assignCasesError, setAssignCasesError] = useState("");

  const roleOptions = [
    { label: 'BM', value: 'BM' },
    { label: 'BCM', value: 'BCM' },
    { label: 'RO', value: 'RO' },
  ];

  useEffect(() => {
    if (!fromBranch || !currentRole) return;
    let mounted = true;
    const fetchReport = async () => {
      setReportLoading(true);
      try {
        const token = readToken();
        const res = await axios.get(
          `${API_BASE_URL}/officer/get-branch-officer-report`,
          {
            params: { page: 1, limit: 1000, is_download: false, branch_code: fromBranch, role_code: currentRole },
            headers: headersWithAuth(token),
          }
        );
        if (mounted) {
          if (res?.data?.status) setOfficerReport(res.data.data);
          else setOfficerReport({ officers: [] });
        }
      } catch (err) {
        if (mounted) setOfficerReport({ officers: [] });
      } finally {
        if (mounted) setReportLoading(false);
      }
    };
    fetchReport();
    return () => {
      mounted = false;
    };
  }, [fromBranch, currentRole]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fromDropdownRef.current &&
        !fromDropdownRef.current.contains(event.target) &&
        fromBranchInputRef.current &&
        !fromBranchInputRef.current.contains(event.target)
      ) {
        setShowFromBranchDropdown(false);
      }

      if (
        toDropdownRef.current &&
        !toDropdownRef.current.contains(event.target) &&
        toBranchInputRef.current &&
        !toBranchInputRef.current.contains(event.target)
      ) {
        setShowToBranchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter branches for "from"
  useEffect(() => {
    if (fromBranchSearch.trim() === '') {
      setFilteredFromBranches([]);
      setShowFromBranchDropdown(false);
    } else {
      const q = fromBranchSearch.toLowerCase();
      const filtered = dropdownOptions.filter(
        (b) =>
          b.value !== toBranch &&
          (b.raw.branch_code?.toLowerCase().includes(q) ||
            b.raw.branch_name?.toLowerCase().includes(q))
      );
      setFilteredFromBranches(filtered);
      setShowFromBranchDropdown(true);
    }
  }, [fromBranchSearch, dropdownOptions, toBranch]);

  // Filter branches for "to"
  useEffect(() => {
    if (toBranchSearch.trim() === '') {
      setFilteredToBranches([]);
      setShowToBranchDropdown(false);
    } else {
      const q = toBranchSearch.toLowerCase();
      const filtered = dropdownOptions.filter(
        (b) =>
          b.value !== fromBranch &&
          (b.raw.branch_code?.toLowerCase().includes(q) ||
            b.raw.branch_name?.toLowerCase().includes(q))
      );
      setFilteredToBranches(filtered);
      setShowToBranchDropdown(true);
    }
  }, [toBranchSearch, dropdownOptions, fromBranch]);

  useEffect(() => {
    if (!currentRole || !fromBranch || !officerReport) return;

    const branch = officerReport.officers?.find(
      (b) => b.branch_code === fromBranch
    );
    if (!branch) return;

    const officers = branch.officer_details || [];

    // Filter officers by selected role
    const matchingRoleOfficers = officers.filter(
      (o) => o.role_code === currentRole
    );

    // Populate employee dropdown
    setEmployeeList(
      matchingRoleOfficers.map((o) => ({
        label: `${o.emp_id} – ${o.first_name}`,
        value: o.emp_id,
        raw: o,
      }))
    );

    // Clear previously selected values when branch/role changes
    setEmployeeId('');
    setFirstName('');
    setCurrentDesignation('');

    // Clear assign cases
    setAssignCasesTo('');
    setAssignCasesOptions([]);

    // also clear validation errors for fields that depend on branch/role
    setErrors((prev) => ({
      ...prev,
      employeeId: undefined,
      firstName: undefined,
      currentDesignation: undefined,
      assignCasesTo: undefined,
    }));

  }, [currentRole, fromBranch, officerReport]);


  useEffect(() => {
     // 1. If employeeId resets -> reset all dependent fields immediately
    if (!employeeId) {
      setFirstName('');
      setCurrentDesignation('');
      setAssignCasesTo('');
      setAssignCasesOptions([]);
      return;
    }

    // 2. Skip until required data is available
    if (!fromBranch || !officerReport) return;

    const branch = officerReport.officers?.find(
      (b) => b.branch_code === fromBranch
    );
    if (!branch) return;

    const officers = branch.officer_details || [];

    // selected officer
    const selectedOfficer = officers.find((o) => o.emp_id === employeeId);

    if (selectedOfficer) {
      setFirstName(selectedOfficer.first_name || '');
      setCurrentDesignation(selectedOfficer.role_code || '');
    }

    const sameRoleOthers = officers
        .filter(
        (o) =>
            o.role_code === currentRole && o.emp_id !== employeeId
        )
        .map((o) => ({
        label: `${o.first_name} (${o.emp_id})`,
        value: o.emp_id,
        }));

    if (sameRoleOthers.length === 0) {
        setAssignCasesOptions([{ label: "No officers available", value: "" }]);
        setAssignCasesError("No officers available under this role.");
    } else {
        setAssignCasesOptions([
        { label: "Select Officer", value: "" },
        ...sameRoleOthers,
        ]);
        setAssignCasesError('');
    }

  }, [employeeId, currentRole, fromBranch, officerReport]);

  useEffect(() => {
    setErrorText('');
    setSuccessText('');
    setSwapResult(null);
    setCanForce(false);
  }, [
    role,
    fromBranch,
    toBranch,
    employeeId,
    firstName,
    currentDesignation,
    assignCasesTo,
  ]);

  const callSwapApi = async (payload) => {
    const token = readToken();
    const res = await axios.put(
      `${API_BASE_URL}/officer/transfer-officer`,
      payload,
      {
        headers: headersWithAuth(token),
      }
    );
    return res.data;
  };

  const callUpgradeApi = async (payload) => {
    const token = readToken();
    const res = await axios.put(
      `${API_BASE_URL}/officer/update-officer-role`,
      payload,
      {
        headers: headersWithAuth(token),
      }
    );
    return res.data;
  };

  const validate = () => {
    const e = {};
    if (!currentRole) e.currentRole = "Current role is required";
    // if (!role) e.role = 'Role is required';

    if (!role) {
        e.role = "Target role is required";
    } else if (role === currentRole) {
        e.role = "Target role must be different from current role";
    }

    if (!fromBranch) e.fromBranch = 'Current branch is required';
    if (!toBranch) e.toBranch = 'New branch is required';
    // if (fromBranch === toBranch)
    //   e.toBranch = 'Current and new branch cannot be same';
    if (!employeeId.trim()) e.employeeId = 'Employee ID is required';
    if (!firstName.trim()) e.firstName = 'First name is required';
    if (!currentDesignation.trim())
      e.currentDesignation = 'Current designation is required';
    if (!assignCasesTo)
      e.assignCasesTo = 'Please select an officer to assign cases'; // NEW VALIDATION
    return e;
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const allErrors = validate();
    setErrors((prev) => ({ ...prev, [field]: allErrors[field] }));
  };

  const handleFromBranchSearchChange = (e) => {
    setFromBranchSearch(e.target.value);
    setFromBranch('');
    setSelectedFromBranchDisplay('');
    // NEW — reset new branch completely
    setToBranch('');
    setSelectedToBranchDisplay('');
    setToBranchSearch('');
    // clear validation
    setErrors((prev) => ({
        ...prev,
        fromBranch: undefined,
        toBranch: undefined,
    }));

    // setErrors((prev) => ({ ...prev, toBranch: undefined }));
    
    // setErrors((prev) => ({ ...prev, fromBranch: undefined }));

  };

  const handleToBranchSearchChange = (e) => {
    setToBranchSearch(e.target.value);
    setToBranch('');
    setSelectedToBranchDisplay('');
    setErrors((prev) => ({ ...prev, toBranch: undefined }));
  };

  const handleFromBranchSelect = (branch) => {
    setFromBranch(branch.value);
    setSelectedFromBranchDisplay(branch.label);
    setFromBranchSearch(branch.label);
    setShowFromBranchDropdown(false);
    
    setShowFromBranchDropdown(false);
    setFilteredFromBranches([]); // prevent "no branches found"

    setToBranch(branch.value);
    setSelectedToBranchDisplay(branch.label);
    setToBranchSearch(branch.label);

    // FIX validation error
    setErrors((prev) => ({ ...prev, fromBranch: undefined }));
    setTouched((prev) => ({ ...prev, fromBranch: true }));

    // FIX — clear validation error for New Branch
    setErrors((prev) => ({ ...prev, toBranch: undefined }));
    setTouched((prev) => ({ ...prev, toBranch: true }));
  };

  const handleToBranchSelect = (branch) => {
    setToBranch(branch.value);
    setSelectedToBranchDisplay(branch.label);
    setToBranchSearch(branch.label);
    setShowToBranchDropdown(false);
    
    // setShowToBranchDropdown(false);
    // setFilteredToBranches([]);

    setErrors((prev) => ({ ...prev, toBranch: undefined }));
    setTouched((prev) => ({ ...prev, toBranch: true }));
  };

  const handleTransfer = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setSubmitLoading(true);
    setErrorText('');
    setSuccessText('');
    setSwapResult(null);
    setCanForce(false);

    const allErrors = validate();
    setErrors(allErrors);
    setTouched({
      role: true,
      fromBranch: true,
      toBranch: true,
      employeeId: true,
      firstName: true,
      currentDesignation: true,
      assignCasesTo: true, // NEW
    });

    if (Object.keys(allErrors).length > 0) {
      setSubmitLoading(false);
      return;
    }

    try {
      const payload = {
        emp_id: employeeId.trim(),
        source_branch_code: fromBranch,
        target_branch_code: toBranch,
        new_role: role,
        handover_responsibility_emp_id: assignCasesTo, // NEW FIELD IN PAYLOAD
        // is_force: false,
        is_force: isForce,
        first_name: firstName.trim(),
        current_designation: currentDesignation.trim(),
      };

      lastPayloadRef.current = payload;
      const res = await callUpgradeApi(payload);

      if (res?.status) {
        setSuccessText(res.message || 'Transfer completed successfully');
        setSwapResult(res.data || null);
        setFromBranch('');
        setToBranch('');
        setCurrentRole('');
        setRole('');
        setEmployeeId('');
        setFirstName('');
        setCurrentDesignation('');
        setAssignCasesTo(''); // NEW
        setFromBranchSearch('');
        setToBranchSearch('');
        setSelectedFromBranchDisplay('');
        setSelectedToBranchDisplay('');
        lastPayloadRef.current = null;
        setCanForce(false);
        toast.success(`${res.message}`);
        closeModal();
        if (typeof onSwapSuccess === 'function') onSwapSuccess(res);
      } else {
        const msg = res?.message || 'Transfer failed';
        setErrorText(msg);
        toast.error(`${msg}`);
        if (/not found/i.test(msg)) setCanForce(true);
      }
    } catch (err) {
      const serverMsg =
        err?.response?.data?.message || err?.message || 'Transfer failed';
      setErrorText(serverMsg);
      toast.error(`${serverMsg}`);
      if (/not found/i.test(serverMsg)) setCanForce(true);
    } finally {
      setSubmitLoading(false);
      setIsForce(false);
    }
  };

  const handleForceSwap = async () => {
    setErrorText('');
    setSuccessText('');
    setSwapResult(null);
    setSubmitLoading(true);
    try {
      const base = lastPayloadRef.current || {
        from_branch_code: fromBranch,
        to_branch_code: toBranch,
        role,
        employee_id: employeeId.trim(),
        first_name: firstName.trim(),
        current_designation: currentDesignation.trim(),
        assign_cases_to: assignCasesTo, // NEW
      };
      const payload = { ...base, is_force: true };
      const res = await callSwapApi(payload);

      if (res?.status) {
        setSuccessText(res.message || 'Force transfer successful');
        setSwapResult(res.data || null);
        setFromBranch('');
        setToBranch('');
        setRole('');
        setEmployeeId('');
        setFirstName('');
        setCurrentDesignation('');
        setAssignCasesTo(''); // NEW
        setFromBranchSearch('');
        setToBranchSearch('');
        setSelectedFromBranchDisplay('');
        setSelectedToBranchDisplay('');
        lastPayloadRef.current = null;
        setCanForce(false);
        if (typeof onSwapSuccess === 'function') onSwapSuccess(res);
      } else {
        setErrorText(res?.message || 'Force transfer failed');
      }
    } catch (err) {
      const serverMsg =
        err?.response?.data?.message || err?.message || 'Force transfer failed';
      setErrorText(serverMsg);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div>
      <div className="modal-overlay" onClick={closeModal} />

      <div className="transfer-modal">
        <div className="modal-header">
          <h5>Update BM, BCM or RO</h5>
          <button className="close-btn" onClick={closeModal} aria-label="Close">
            <IoMdClose />
          </button>
        </div>

        <p className="modal-subtitle">
          Update a BM, BCM or RO role in same branch
        </p>

        <form className="transfer-modal-content" onSubmit={handleTransfer}>
          {/* SELECT ROLE */}
          <div className="form-group-row">
            <div>
                <label>Select Current Role</label>
                <select
                    value={currentRole}
                    onChange={(e) => {
                        setCurrentRole(e.target.value);
                        // clear dependent fields
                        setEmployeeId('');
                        setFirstName('');
                        setCurrentDesignation('');
                        setAssignCasesTo('');
                        setAssignCasesOptions([]);
                    }}
                    onBlur={handleBlur('currentRole')}
                >
                    <option value="">Select Role</option>
                    {roleOptions.map((r) => (
                        <option key={r.value} value={r.value}>
                        {r.label}
                        </option>
                    ))}
                </select>
                {touched.currentRole && errors.currentRole && (
                    <div className="error-text">{errors.currentRole}</div>
                )}
            </div>

            <div>
              <label>Select Target Role</label>
              <select
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  setErrors((prev) => ({ ...prev, role: undefined }));
                }}
                onBlur={handleBlur('role')}
                >
                <option value="">Select Role</option>
                {roleOptions.map((r) => (
                    <option key={r.value} value={r.value}
                      disabled={r.value === currentRole}   // 💥 disables current role
                    >
                    {r.label}
                  </option>
                ))}
              </select>
              {touched.role && errors.role && (
                <div className="error-text">{errors.role}</div>
              )}
            </div>
          </div>

          {/* CURRENT BRANCH AND NEW BRANCH */}
          <div className="form-group-row-two">
            {/* CURRENT BRANCH */}
            <div className="branch-search-container">
              <label>Current Branch</label>
              <div className="search-input-wrapper" ref={fromDropdownRef}>
                <input
                  ref={fromBranchInputRef}
                  type="text"
                  placeholder={
                    reportLoading
                      ? 'Loading branches...'
                      : 'Type to search branch by name or code...'
                  }
                  value={fromBranchSearch}
                  onChange={handleFromBranchSearchChange}
                  onFocus={() =>
                    fromBranchSearch && setShowFromBranchDropdown(true)
                  }
                  onBlur={handleBlur('fromBranch')}
                  disabled={reportLoading}
                  className="branch-search-input"
                  autoComplete="off"
                />
                <span className="search-icon-right" aria-hidden="true">
                  <IoSearch />
                </span>

                {showFromBranchDropdown &&
                  fromBranchSearch.trim() !== '' &&
                  !reportLoading && (
                    <div className="branch-dropdown-results">
                      {filteredFromBranches.length > 0 ? (
                        filteredFromBranches.map((branch) => (
                          <div
                            key={branch.value}
                            className={`branch-dropdown-item ${
                              fromBranch === branch.value ? 'selected' : ''
                            }`}
                            onClick={() => handleFromBranchSelect(branch)}
                          >
                            <div className="branch-item-name">
                              <strong>{branch.raw.branch_name}</strong>
                            </div>
                            <div className="branch-item-code">
                              Branch Code: {branch.value}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="branch-dropdown-item no-results">
                          No branches found
                        </div>
                      )}
                    </div>
                  )}
              </div>

              {selectedFromBranchDisplay && (
                <small className="selected-branch-info">
                  ✓ Selected: {selectedFromBranchDisplay}
                </small>
              )}

              {touched.fromBranch && errors.fromBranch && (
                <div className="error-text">{errors.fromBranch}</div>
              )}
            </div>

            {/* NEW BRANCH */}
            <div className="branch-search-container">
              <label>New Branch</label>
              <div className="search-input-wrapper" ref={toDropdownRef}>
                <input
                  ref={toBranchInputRef}
                  type="text"
                  placeholder={
                    reportLoading
                      ? 'Loading branches...'
                      : 'Type to search branch by name or code...'
                  }
                  value={toBranchSearch}
                  onChange={handleToBranchSearchChange}
                  onFocus={() =>
                    toBranchSearch && setShowToBranchDropdown(true)
                  }
                  onBlur={handleBlur('toBranch')}
                //   disabled={reportLoading}
                  disabled
                  className="branch-search-input"
                  autoComplete="off"
                />
                <span className="search-icon-right" aria-hidden="true">
                  <IoSearch />
                </span>

                {false && showToBranchDropdown &&
                  toBranchSearch.trim() !== '' &&
                  !reportLoading && (
                    <div className="branch-dropdown-results">
                      {filteredToBranches.length > 0 ? (
                        filteredToBranches.map((branch) => (
                          <div
                            key={branch.value}
                            className={`branch-dropdown-item ${
                              toBranch === branch.value ? 'selected' : ''
                            }`}
                            onClick={() => handleToBranchSelect(branch)}
                          >
                            <div className="branch-item-name">
                              <strong>{branch.raw.branch_name}</strong>
                            </div>
                            <div className="branch-item-code">
                              Branch Code: {branch.value}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="branch-dropdown-item no-results">
                          No branches found
                        </div>
                      )}
                    </div>
                  )}
              </div>

              {selectedToBranchDisplay && (
                <small className="selected-branch-info">
                  ✓ Selected: {selectedToBranchDisplay}
                </small>
              )}

              {touched.toBranch && errors.toBranch && (
                <div className="error-text">{errors.toBranch}</div>
              )}
            </div>
          </div>

          {/* EMPLOYEE ID */}
          <div className="form-group-row">
            <div>
              <label>Employee ID</label>
              {/* <input
                type="text"
                placeholder="Enter employee ID"
                value={employeeId}
                onChange={(e) => {
                  setEmployeeId(e.target.value);
                  setErrors((prev) => ({ ...prev, employeeId: undefined }));
                }}
                onBlur={handleBlur('employeeId')}
              /> */}
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              >
                <option value="">Select Employee</option>
                {employeeList.map((emp) => (
                  <option key={emp.value} value={emp.value}>
                    {emp.label}
                  </option>
                ))}
              </select>
              {touched.employeeId && errors.employeeId && (
                <div className="error-text">{errors.employeeId}</div>
              )}
            </div>
          </div>

          {/* FIRST NAME */}
          <div className="form-group-row">
            <div>
              <label>First Name</label>
              <input
                type="text"
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setErrors((prev) => ({ ...prev, firstName: undefined }));
                }}
                onBlur={handleBlur('firstName')}
              />
              {touched.firstName && errors.firstName && (
                <div className="error-text">{errors.firstName}</div>
              )}
            </div>
          </div>

          {/* CURRENT DESIGNATION */}
          <div className="form-group-row">
            <div>
              <label>Current Designation</label>
              <input
                type="text"
                placeholder="Enter current designation"
                value={currentDesignation}
                onChange={(e) => {
                  setCurrentDesignation(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    currentDesignation: undefined,
                  }));
                }}
                onBlur={handleBlur('currentDesignation')}
              />
              {touched.currentDesignation && errors.currentDesignation && (
                <div className="error-text">{errors.currentDesignation}</div>
              )}
            </div>
          </div>

          {/* NEW FIELD: ASSIGN EXISTING CASES TO */}
          <div className="form-group-row">
            <div>
              <label>Assign Existing Cases To</label>
              <select
                value={assignCasesTo}
                onChange={(e) => {
                  setAssignCasesTo(e.target.value);
                  setErrors((prev) => ({ ...prev, assignCasesTo: undefined }));
                }}
                onBlur={handleBlur('assignCasesTo')}
              >
                {assignCasesOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {assignCasesError && (
                <div className="error-text">{assignCasesError}</div>
              )}
              {touched.assignCasesTo && errors.assignCasesTo && (
                <div className="error-text">{errors.assignCasesTo}</div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px",  width: "50%" }}>
            <input
              id="force-transfer"
              type="checkbox"
              checked={isForce}
              onChange={(e) => {
                setIsForce(e.target.checked);
              }}
              style={{ width: "10%" }}
            />
            <label htmlFor="force-transfer">Force Transfer</label>
          </div>

          {swapResult && (
            <div className="swap-result">
              <h6>Transfer Result</h6>
              <div>Role: {swapResult.swapped_role || role}</div>
              <div>Field updated: {swapResult.field_updated || '-'}</div>

              <div style={{ marginTop: 8 }}>
                <strong>From:</strong>
                <div>Officer ID: {swapResult.from?.officer_id || '-'}</div>
                <div>
                  Old branch: {swapResult.from?.old_branch || fromBranch}
                </div>
                <div>New branch: {swapResult.from?.new_branch || toBranch}</div>
              </div>

              <div style={{ marginTop: 8 }}>
                <strong>To:</strong>
                <div>Officer ID: {swapResult.to?.officer_id || '-'}</div>
                <div>Old branch: {swapResult.to?.old_branch || toBranch}</div>
                <div>New branch: {swapResult.to?.new_branch || fromBranch}</div>
              </div>
            </div>
          )}

           {errorText && <div className="error-message">{errorText}</div>}
            {successText && <div className="success-message">{successText}</div>}

          <div className="modal-actions">
            <button
              type="submit"
              className="transfer-user-btn"
              disabled={submitLoading || reportLoading}
            >
              {submitLoading ? 'Processing...' : 'Update User'}
            </button>

            {canForce && (
              <button
                type="button"
                className="transfer-user-btn force-btn"
                onClick={handleForceSwap}
                disabled={submitLoading}
              >
                {submitLoading ? 'Processing...' : 'Force Transfer Anyway'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpgradeModal;